import React, { useMemo } from 'react'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import {
  ASSIGNMENT_STATE_AUTO_1,
  ASSIGNMENT_STATE_AUTO_2,
  ASSIGNMENT_STATE_AUTO_3,
  ASSIGNMENT_STATE_BLIND_PLUS,
  ASSIGNMENT_STATE_MISSING,
} from 'lib/assignmentStates'

import CarCardAdditionalDetails from 'components/atoms/CarCardAdditionalDetails'
import CarCardAdditionalDetailsItem from 'components/atoms/CarCardAdditionalDetailsItem'

import {
  getCarFinalPrice,
  getAssignmentState,
  getAssignmentStatusColor,
  getAutoAssignmentDisplayState,
} from './assignmentLib'

export default function AssignmentCarAdditionalDetails({
  vertical,
  partners,
  car,
  sessionState,
}) {
  const { t } = useTranslation()

  const numberFormatter = new Intl.NumberFormat('it-IT')

  const assignmentState = useMemo(() => getAssignmentState(car), [car])

  const partnerName = useMemo(
    () =>
      car?.assignedPartnerId &&
      partners.find((partner) => partner.partnerId === car.assignedPartnerId)
        ?.partnerName,
    [car, partners]
  )

  const firstProposalPartnerName = useMemo(
    () =>
      car?.autoAssignmentPartnerId &&
      partners.find(
        (partner) => partner.partnerId === car.autoAssignmentPartnerId
      )?.partnerName,
    [car, partners]
  )

  const firstProposalAuto = useMemo(
    () =>
      [
        ASSIGNMENT_STATE_AUTO_1,
        ASSIGNMENT_STATE_AUTO_2,
        ASSIGNMENT_STATE_AUTO_3,
      ].includes(car.autoAssignmentState),
    [car]
  )

  const finalPrice = useMemo(() => getCarFinalPrice(car), [car])
  const autoAssignmentDisplayState = useMemo(
    () => getAutoAssignmentDisplayState(car),
    [car]
  )
  const reservePrice = useMemo(() => {
    if (
      autoAssignmentDisplayState === ASSIGNMENT_STATE_BLIND_PLUS &&
      finalPrice !== null &&
      finalPrice !== undefined
    ) {
      return finalPrice
    }

    return car?.reservePrice
  }, [autoAssignmentDisplayState, car?.reservePrice, finalPrice])

  const finalPriceNumber = Number(finalPrice)
  const reservePriceNumber = Number(reservePrice)
  const hasFinalPrice = finalPrice !== null && finalPrice !== undefined
  const finalPriceValue = hasFinalPrice ? finalPrice : '-'
  const isFinalPriceBelowReserve =
    Number.isFinite(finalPriceNumber) &&
    Number.isFinite(reservePriceNumber) &&
    finalPriceNumber < reservePriceNumber

  const blindOffersTotal = useMemo(() => {
    if (
      car?.blindOfferPartnersCount !== null &&
      car?.blindOfferPartnersCount !== undefined
    ) {
      return Number(car.blindOfferPartnersCount) || 0
    }

    return car?.assignmentType === 'blind'
      ? Number(car.firstChoiceTotal || 0)
      : 0
  }, [car])

  const firstChoiceTotal = useMemo(
    () =>
      car?.firstChoicePartnersCount !== null &&
      car?.firstChoicePartnersCount !== undefined
        ? Number(car.firstChoicePartnersCount) || 0
        : car?.assignmentType === 'blind'
        ? 0
        : Number(car.firstChoiceTotal || 0),
    [car]
  )

  return (
    <CarCardAdditionalDetails vertical={vertical}>
      {vertical && (
        <CarCardAdditionalDetailsItem
          vertical={vertical}
          title={t('assignment.additionalTitles.finalPrice')}
          value={(hasFinalPrice && numberFormatter.format(finalPrice)) || '-'}
        />
      )}
      <CarCardAdditionalDetailsItem
        vertical={vertical}
        title={t('assignment.additionalTitles.status')}
        value={t(`assignment.status.${sessionState}.${assignmentState}`)}
        valueProps={{
          color: getAssignmentStatusColor(assignmentState),
        }}
      />
      {vertical && <Box p={1} />}
      {!vertical && (
        <CarCardAdditionalDetailsItem
          vertical={vertical}
          title={t('assignment.additionalTitles.finalPrice')}
          value={finalPriceValue}
          valueProps={{
            color: isFinalPriceBelowReserve ? 'error' : undefined,
          }}
        />
      )}
      {!vertical && (
        <CarCardAdditionalDetailsItem
          vertical={vertical}
          title={t('assignment.additionalTitles.partner')}
          value={partnerName || '-'}
        />
      )}
      <CarCardAdditionalDetailsItem
        vertical={vertical}
        title={t('assignment.additionalTitles.firstChoice')}
        value={`${firstChoiceTotal} ${t(
          'assignment.additionalValues.partner'
        )}`}
      />
      <CarCardAdditionalDetailsItem
        vertical={vertical}
        title={t('assignment.additionalTitles.blindOffers')}
        value={`${blindOffersTotal} ${t(
          'assignment.additionalValues.partner'
        )}`}
      />
      {vertical && <Box p={1} />}
      {vertical && (
        <CarCardAdditionalDetailsItem
          vertical={vertical}
          title={t('assignment.additionalTitles.firstProposal')}
          value={t(
            `assignment.status.${sessionState}.${
              firstProposalAuto
                ? getAutoAssignmentDisplayState(car)
                : ASSIGNMENT_STATE_MISSING
            }`
          )}
        />
      )}
      {vertical && firstProposalAuto && (
        <CarCardAdditionalDetailsItem
          vertical={vertical}
          title={t('assignment.additionalTitles.firstProposalPartner')}
          value={firstProposalPartnerName}
        />
      )}
    </CarCardAdditionalDetails>
  )
}

AssignmentCarAdditionalDetails.propTypes = {
  vertical: T.bool,
  partners: T.array,
  car: T.object.isRequired,
  sessionState: T.string.isRequired,
}
